import React, { useState, useCallback } from "react";
import {
  AppProvider,
  LegacyCard,
  TextField,
  Select,
  InlineGrid,
  Spinner,
  Button,
  Icon,
} from "@shopify/polaris";
import en from "@shopify/polaris/locales/en.json";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from '@shopify/polaris-icons';

const STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado",
  "Connecticut", "Delaware", "Florida", "Georgia", "Hawaii", "Idaho",
  "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana", "Maine",
  "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey",
  "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
  "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia",
  "Washington", "West Virginia", "Wisconsin", "Wyoming"
];

const COUNTRIES = ["United States", "Canada"];
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SalesRepAdd() {
  const baseUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    gender: "Male",
    email: "",
    password: "",
    phone: "",
    address: "",
    city: "",
    state: "Alabama",
    pincode: "",
    country: "United States",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [messStatus, setMessStatus] = useState("");

  const handleBack = () => navigate(-1);

  const handleChange = useCallback((field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const validate = () => {
    const requiredFields = [
      "name", "email", "password", "phone",
      "address", "city", "state", "pincode", "country"
    ];
    const newErrors = {};

    requiredFields.forEach((field) => {
      if (!form[field]?.trim()) {
        newErrors[field] = "This field is required";
      }
    });

    if (form.email && !EMAIL_REGEX.test(form.email)) {
      newErrors.email = "Enter a valid email";
    }

    if (form.password && form.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const token = localStorage.getItem("token");
    if (!token) {
      setMessStatus("User is not authenticated.");
      return;
    }

    const adminrole = jwtDecode(token)?.role;
    setLoading(true);

    try {
      const response = await fetch(`${baseUrl}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...form, adminrole }),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.message || "Create failed!");

      setMessStatus(`Sales Reps has been added successfully.`);
      navigate("/dashboard/sales-reps");
    } catch (error) {
      setMessStatus(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit();
  };

  return (
    <AppProvider i18n={en}>
      <div className="button_option">
        <h2>
          <span onClick={handleBack}>
            <Icon source={ArrowLeftIcon} tone="base" />
          </span>{" "}
          Add Sales Rep
        </h2>
      </div>

      <form onSubmit={handleFormSubmit}>
        <LegacyCard title="Basic Information" sectioned>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 2, lg: 3 }} gap="400">
            <TextField
              label="Full Name"
              value={form.name}
              onChange={handleChange("name")}
              error={errors.name}
            />
            <Select
              label="Gender"
              options={["Male", "Female"]}
              onChange={handleChange("gender")}
              value={form.gender}
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={handleChange("email")}
              error={errors.email}
              type="email"
              autoComplete="email"
            />
            <TextField
              label="Password"
              value={form.password}
              onChange={handleChange("password")}
              error={errors.password}
              type="password"
              autoComplete="new-password"
            />
            <TextField
              label="Phone Number"
              value={form.phone}
              onChange={handleChange("phone")}
              error={errors.phone}
              type="number"
            />
            <TextField
              label="Address"
              value={form.address}
              onChange={handleChange("address")}
              error={errors.address}
            />
            <TextField
              label="City"
              value={form.city}
              onChange={handleChange("city")}
              error={errors.city}
            />
            <Select
              label="State"
              options={STATES}
              onChange={handleChange("state")}
              value={form.state}
            />
            <TextField
              label="Zip Code"
              value={form.pincode}
              onChange={handleChange("pincode")}
              error={errors.pincode}
            />
            <Select
              label="Country"
              options={COUNTRIES}
              onChange={handleChange("country")}
              value={form.country}
              error={errors.country}
            />
          </InlineGrid>
        </LegacyCard>

        {messStatus && (
          <p className='sucess_mess'>{messStatus}</p>
        )}

        <div className="customerAddBtn">
          {!loading ? (
            <Button variant="primary" submit>
              Save
            </Button>
          ) : (
            <Spinner accessibilityLabel="Adding customer" size="small" />
          )}
        </div>
      </form>
    </AppProvider>
  );
}
